'use client';

import { useSyncExternalStore } from 'react';

import type { ResolvedTheme, Theme } from './theme-provider-minimal';

const THEME_CHANGE = 'naumu-theme-change';
const DARK_QUERY = '(prefers-color-scheme: dark)';
let temporaryTheme: Theme | null = null;

/**
 * The stored preference, resolved exactly like the pre-paint bootstrap script
 * (apps/web `theme-bootstrap.tsx`, apps/marketing `layout.tsx`): `light` and
 * `dark` are honoured, anything else (nothing stored, an unknown value, no
 * storage) follows the OS. The two must agree, or the page repaints right
 * after hydration for a visitor who never chose a theme.
 */
const readTheme = (): Theme => {
	if (temporaryTheme !== null) return temporaryTheme;
	try {
		const stored = localStorage.getItem('theme');
		return stored === 'light' || stored === 'dark' ? stored : 'system';
	} catch {
		return 'system';
	}
};

const subscribe = (onChange: () => void) => {
	const media = window.matchMedia(DARK_QUERY);
	const onStorage = (event: StorageEvent) => {
		if (event.key === 'theme' || event.key === null) {
			temporaryTheme = null;
			onChange();
		}
	};
	window.addEventListener('storage', onStorage);
	window.addEventListener(THEME_CHANGE, onChange);
	media.addEventListener('change', onChange);
	return () => {
		window.removeEventListener('storage', onStorage);
		window.removeEventListener(THEME_CHANGE, onChange);
		media.removeEventListener('change', onChange);
	};
};

// A primitive snapshot stays referentially stable between store changes.
const getSnapshot = () => `${readTheme()}:${window.matchMedia(DARK_QUERY).matches ? 'dark' : 'light'}`;
const getServerSnapshot = (): null => null;

/**
 * What the server render and the hydration pass report while the browser
 * preference is not readable yet. It has to be one constant on both sides
 * (a hydration pass that differs from the server HTML is a mismatch), so it
 * cannot be the visitor's real preference: `system` is the theme most
 * visitors have, and `dark` is the design's first mode. Anything that acts
 * on the theme (painting a canvas, toggling the `dark` class) waits for
 * `ready` instead of trusting this pass.
 */
const SERVER_FALLBACK = 'system:dark';

const setTheme = (theme: Theme) => {
	try {
		localStorage.setItem('theme', theme);
		temporaryTheme = null;
	} catch {
		// Storage can be disabled or full. Keep the current session usable.
		temporaryTheme = theme;
	}
	// Native storage events only reach OTHER documents. Notify every provider
	// and consumer here too (the root toaster lives above the settings provider).
	window.dispatchEvent(new Event(THEME_CHANGE));
};

/**
 * Read at each consumer so late-hydrating Suspense children retain the SSR
 * value. `ready` is false for the server render and the hydration pass only;
 * from the first client pass on, `theme` and `resolvedTheme` are the visitor's.
 */
export const useThemeSnapshot = () => {
	const snapshot = useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);
	const [theme, systemTheme] = (snapshot ?? SERVER_FALLBACK).split(':') as [Theme, ResolvedTheme];
	return {
		theme,
		resolvedTheme: theme === 'system' ? systemTheme : theme,
		ready: snapshot !== null,
		setTheme,
	};
};
